import { JSX, useEffect, useMemo, useState } from 'react';
import { UseFormReturn, useFieldArray, useWatch } from 'react-hook-form';

import { PlusIcon, TrashIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { Label } from 'components/Label';
import { Select, SelectValue } from 'components/Select';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { AccessorialLookupItem } from 'lib/api/getEquipmentLookupsGlobalTranz';
import { CostLineItem, NormalizedLoad } from 'types/Load';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

/** accessorialTypeId 0 = rate/charge types in GlobalTranz AllAccessorials API */
const RATE_TYPE_ACCESSORIAL_TYPE_ID = 0;

interface FinancialsFormProps {
  formMethods: UseFormReturn<NormalizedLoad>;
  /** TMS integration ID; when set, fetches rate types (accessorialTypeId 0) for dropdown if accessorials not provided */
  tmsID?: number;
  /** When provided (from bootstrap), used for rate type dropdown instead of fetching */
  accessorials?: AccessorialLookupItem[];
}

const BASE_RATE_ACCESSORIAL_ID = 1;

/** Row 1 only: always Base Rate */
const createBaseRateLineItem = (): CostLineItem => ({
  label: 'Base Rate',
  unitBasis: 'Flat',
  quantity: 1,
  ratePerUnitUSD: 0,
  totalChargeUSD: 0,
  note: '',
  accessorialId: BASE_RATE_ACCESSORIAL_ID,
});

/** New rows (Add Row): empty description so user selects from dropdown */
const createEmptyLineItem = (): CostLineItem => ({
  label: '',
  unitBasis: 'Flat',
  quantity: 1,
  ratePerUnitUSD: 0,
  totalChargeUSD: 0,
  note: '',
  accessorialId: 0,
});

export function FinancialsForm({
  formMethods,
  tmsID: _tmsID,
  accessorials: providedAccessorials,
}: FinancialsFormProps): JSX.Element {
  const { control, setValue } = formMethods;
  const { aiDefaultValues } = useExtendedFormContext();
  const [rateTypeOptions, setRateTypeOptions] = useState<
    AccessorialLookupItem[]
  >([]);

  // Rate types (accessorialTypeId 0) from bootstrap accessorials
  useEffect(() => {
    if (!providedAccessorials?.length) {
      setRateTypeOptions([]);
      return;
    }
    const rateTypes = providedAccessorials.filter(
      (a) => a.accessorialTypeId === RATE_TYPE_ACCESSORIAL_TYPE_ID
    );
    setRateTypeOptions(rateTypes);
  }, [providedAccessorials]);

  // Customer (Revenue) line items
  const {
    fields: customerLineItems,
    append: customerLineItemsAppend,
    remove: customerLineItemsRemove,
  } = useFieldArray({
    control,
    name: 'rateData.customerLineItems',
  });

  // Carrier (Cost) line items
  const {
    fields: carrierLineItems,
    append: carrierLineItemsAppend,
    remove: carrierLineItemsRemove,
  } = useFieldArray({
    control,
    name: 'rateData.carrierLineItems',
  });

  // Watch values for calculations - use useWatch for better reactivity with nested arrays
  const watchedCustomerLineItems = useWatch({
    control,
    name: 'rateData.customerLineItems',
  });
  const watchedCarrierLineItems = useWatch({
    control,
    name: 'rateData.carrierLineItems',
  });
  const watchedCustomerTotal =
    useWatch({
      control,
      name: 'rateData.customerTotalCharge.val' as const,
    }) || 0;

  // Initialize with default Base Rate row (row 1) if empty
  useEffect(() => {
    if (customerLineItems.length === 0) {
      customerLineItemsAppend(createBaseRateLineItem());
    }
    if (carrierLineItems.length === 0) {
      carrierLineItemsAppend(createBaseRateLineItem());
    }
  }, [
    customerLineItems.length,
    carrierLineItems.length,
    customerLineItemsAppend,
    carrierLineItemsAppend,
  ]);

  // Calculate totals
  const totalCustomerRevenue = useMemo(() => {
    const items = watchedCustomerLineItems ?? [];

    return items.reduce((sum, item) => sum + (item.totalChargeUSD || 0), 0);
  }, [watchedCustomerLineItems]);

  const totalCarrierCost = useMemo(() => {
    const items = watchedCarrierLineItems ?? [];

    return items.reduce((sum, item) => sum + (item.totalChargeUSD || 0), 0);
  }, [watchedCarrierLineItems]);

  // Calculate margin
  const margin = useMemo(() => {
    return totalCustomerRevenue - totalCarrierCost;
  }, [totalCustomerRevenue, totalCarrierCost]);

  const marginPercent = useMemo(() => {
    if (totalCustomerRevenue === 0) return 0;
    return ((margin / totalCustomerRevenue) * 100).toFixed(2);
  }, [margin, totalCustomerRevenue]);

  // Auto-update customer total charge (but not carrier cost - that's manual)
  useEffect(() => {
    if (totalCustomerRevenue !== watchedCustomerTotal) {
      setValue('rateData.customerTotalCharge.val', totalCustomerRevenue, {
        shouldDirty: aiDefaultValues,
      });
    }
  }, [totalCustomerRevenue, watchedCustomerTotal, setValue, aiDefaultValues]);

  // Note: Target Cost (carrierCost) is manually entered, not auto-calculated
  // The totalCarrierCost is calculated from line items but doesn't overwrite the manual Target Cost field

  // Add row to both sections simultaneously (empty description; user selects in Charges)
  const handleAddRow = () => {
    customerLineItemsAppend({ ...createEmptyLineItem() });
    carrierLineItemsAppend({ ...createEmptyLineItem() });
  };

  // Remove row from both sections; never allow removing row 0 (Base Rate)
  const handleRemoveRow = (index: number) => {
    if (
      index === 0 ||
      customerLineItems.length <= 1 ||
      carrierLineItems.length <= 1
    ) {
      return;
    }
    customerLineItemsRemove(index);
    carrierLineItemsRemove(index);
  };

  return (
    <div className='space-y-4'>
      {/* Top Fields - Compact 2-column layout for sidebar */}
      <Grid cols='2' gap='sm' className='mx-0 w-full'>
        <div className='relative'>
          <RHFTextInput
            name='rateData.carrierMaxRate'
            label='Max Buy'
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
            placeholder='0.00'
            inputClassName='text-sm'
          />
        </div>
        <div className='relative'>
          <RHFTextInput
            name='rateData.carrierCost'
            label='Target Cost'
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
            placeholder='0.00'
            inputClassName='text-sm'
          />
        </div>
        <div className='relative'>
          <RHFTextInput
            name='rateData.priceToBeat'
            label='Price To Beat'
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
            placeholder='0.00'
            inputClassName='text-sm'
          />
        </div>
        <div className='relative'>
          <RHFTextInput
            name='rateData.rate'
            label='Rate'
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
            placeholder='0.00'
            inputClassName='text-sm'
          />
        </div>
        <RHFTextInput
          name='rateData.miles'
          label='Miles'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
          placeholder='Enter Miles'
          inputClassName='text-sm'
        />
      </Grid>

      {/* Charges Section - Stacked vertically for sidebar */}
      <div className='space-y-3'>
        <div className='flex items-center justify-between'>
          <Typography
            variant='subtitle2'
            className='text-neutral-700 font-medium'
          >
            Charges
          </Typography>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={handleAddRow}
            className='h-7 px-2 text-xs'
            buttonNamePosthog={ButtonNamePosthog.CreateCommodity}
          >
            <PlusIcon className='h-3 w-3 mr-1' />
            Add Row
          </Button>
        </div>

        <div className='space-y-2'>
          {carrierLineItems.map((field, index) => (
            <div
              key={field.id}
              className='border border-neutral-200 rounded p-2 space-y-2 bg-neutral-50/50'
            >
              <div className='flex items-center justify-between'>
                <Flex align='center' gap='sm'>
                  <Typography
                    variant='body-xs'
                    className='text-neutral-600 font-medium'
                  >
                    Row {index + 1}
                  </Typography>
                  {index === 0 && (
                    <Typography variant='body-xs' className='text-error-500'>
                      Required
                    </Typography>
                  )}
                </Flex>
                {index > 0 && carrierLineItems.length > 1 && (
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() => handleRemoveRow(index)}
                    className='h-6 w-6 p-0 text-destructive hover:text-destructive'
                    buttonNamePosthog={ButtonNamePosthog.ClearForm}
                  >
                    <TrashIcon className='h-3 w-3' />
                  </Button>
                )}
              </div>
              <Grid cols='2' gap='sm' className='mx-0 w-full'>
                <FinancialsTableRow
                  index={index}
                  fieldPath='rateData.carrierLineItems'
                  formMethods={formMethods}
                  rateTypeOptions={rateTypeOptions}
                  descriptionEditable={true}
                  isRow1={index === 0}
                  isCompact={true}
                />
              </Grid>
            </div>
          ))}
        </div>
      </div>

      {/* Revenue Section - Stacked vertically for sidebar */}
      <div className='space-y-3'>
        <Typography
          variant='subtitle2'
          className='text-neutral-700 font-medium'
        >
          Revenue
        </Typography>

        <div className='space-y-2'>
          {customerLineItems.map((field, index) => (
            <div
              key={field.id}
              className='border border-neutral-200 rounded p-2 space-y-2 bg-neutral-50/50'
            >
              <div className='flex items-center justify-between'>
                <Flex align='center' gap='sm'>
                  <Typography
                    variant='body-xs'
                    className='text-neutral-600 font-medium'
                  >
                    Row {index + 1}
                  </Typography>
                  {index === 0 && (
                    <Typography variant='body-xs' className='text-error-500'>
                      Required
                    </Typography>
                  )}
                </Flex>
              </div>
              <Grid cols='2' gap='sm' className='mx-0 w-full'>
                <FinancialsTableRow
                  index={index}
                  fieldPath='rateData.customerLineItems'
                  formMethods={formMethods}
                  rateTypeOptions={rateTypeOptions}
                  descriptionEditable={false}
                  isRow1={index === 0}
                  isCompact={true}
                />
              </Grid>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Section - Compact for sidebar */}
      <div className='pt-3 border-t border-neutral-200 space-y-2'>
        <Grid cols='2' gap='sm' className='mx-0 w-full'>
          <div className='space-y-1'>
            <Typography variant='body-xs' className='text-neutral-600'>
              Total Cost
            </Typography>
            <Typography variant='body-sm' className='font-medium'>
              {totalCarrierCost.toFixed(2)}
            </Typography>
          </div>
          <div className='space-y-1'>
            <Typography variant='body-xs' className='text-neutral-600'>
              Total Revenue
            </Typography>
            <Typography variant='body-sm' className='font-medium'>
              {totalCustomerRevenue.toFixed(2)}
            </Typography>
          </div>
          <div className='space-y-1'>
            <Typography variant='body-xs' className='text-neutral-600'>
              Margin
            </Typography>
            <Typography variant='body-sm' className='font-medium'>
              {margin.toFixed(2)}
            </Typography>
          </div>
          <div className='space-y-1'>
            <Typography variant='body-xs' className='text-neutral-600'>
              Margin%
            </Typography>
            <Typography variant='body-sm' className='font-medium'>
              {marginPercent}%
            </Typography>
          </div>
        </Grid>
      </div>
    </div>
  );
}

interface FinancialsTableRowProps {
  index: number;
  fieldPath: 'rateData.customerLineItems' | 'rateData.carrierLineItems';
  formMethods: UseFormReturn<NormalizedLoad>;
  rateTypeOptions: AccessorialLookupItem[];
  /** Charges section: dropdown drives description; Revenue: read-only (synced from Charges) */
  descriptionEditable: boolean;
  /** Row 1: always Base Rate, dropdown disabled */
  isRow1: boolean;
  isCompact?: boolean; // For sidebar compact layout
}

function FinancialsTableRow({
  index,
  fieldPath,
  formMethods,
  rateTypeOptions,
  descriptionEditable,
  isRow1,
  isCompact = false,
}: FinancialsTableRowProps): JSX.Element {
  const { control, setValue } = formMethods;
  const isCarrierCostField = fieldPath.includes('carrier');

  const carrierFieldOptions = {
    valueAsNumber: true,
    min: 0,
  };

  const revenueFieldOptions = {
    valueAsNumber: true,
    min: 0.01,
    required: 'This field is required',
  };

  const accessorialId = useWatch({
    control,
    name: `${fieldPath}.${index}.accessorialId` as const,
  });
  const label = useWatch({
    control,
    name: `${fieldPath}.${index}.label` as const,
  });

  // Dropdown options: exclude Base Rate (row 1 only); other rows select from Detention, etc.
  const rateOptions =
    rateTypeOptions.length > 0
      ? rateTypeOptions
          .filter((a) => a.accessorialId !== BASE_RATE_ACCESSORIAL_ID)
          .map((a) => ({
            value: String(a.accessorialId),
            label: a.displayText,
          }))
      : [];

  let descriptionValue = '';
  if (accessorialId != null && accessorialId !== 0) {
    descriptionValue = String(accessorialId);
  } else if (label) {
    descriptionValue =
      rateOptions.find((option) => option.label === label)?.value ?? '';
  }

  // When user selects in Charges dropdown, update both carrier and customer at this index
  const onDescriptionChange = (value: SelectValue) => {
    if (typeof value !== 'string') {
      return; // ignore undefined / arrays safely
    }

    const num = parseInt(value, 10);
    const accessorialIdVal = Number.isNaN(num) ? 0 : num;

    const option = rateOptions.find((o) => o.value === value);
    const labelVal = option?.label ?? '';

    setValue(`${fieldPath}.${index}.accessorialId` as const, accessorialIdVal, {
      shouldDirty: true,
    });

    setValue(`${fieldPath}.${index}.label` as const, labelVal, {
      shouldDirty: true,
    });

    // Sync to Revenue (customer) when editing in Charges (carrier)
    if (fieldPath === 'rateData.carrierLineItems') {
      setValue(
        `rateData.customerLineItems.${index}.accessorialId` as const,
        accessorialIdVal,
        {
          shouldDirty: true,
        }
      );
      setValue(`rateData.customerLineItems.${index}.label` as const, labelVal, {
        shouldDirty: true,
      });
    }
  };

  // Note: Since Units and Rate are readonly, we don't auto-calculate the total
  // Users can directly enter the Cost/Revenue total value

  // Revenue row 2+: disabled Select with same options as Charges so UI matches (shows synced value)
  const revenueDescriptionOptions =
    rateOptions.length > 0
      ? [{ value: '', label: '—' }, ...rateOptions]
      : [{ value: '', label: '—' }];

  // Base Rate Select (row 1): shared by compact and horizontal layouts
  const baseRateSelectOptions = [{ value: '1', label: 'Base Rate' }];
  const renderBaseRateSelect = (inputClassName: string) => (
    <Select
      className={inputClassName}
      value='1'
      options={baseRateSelectOptions}
      disabled={true}
      onChange={() => {}}
    />
  );

  const renderDescriptionField = (inputClassName: string) => {
    if (isRow1) {
      return renderBaseRateSelect(inputClassName);
    }
    if (!descriptionEditable) {
      return (
        <Select
          className={inputClassName}
          value={descriptionValue}
          options={revenueDescriptionOptions}
          disabled={true}
          onChange={() => {}}
        />
      );
    }
    return (
      <Select
        showSearch
        className={inputClassName}
        placeholder='Choose'
        onChange={onDescriptionChange}
        value={descriptionValue}
        options={rateOptions}
      />
    );
  };

  if (isCompact) {
    return (
      <>
        {/* Description: Row 1 = disabled Base Rate (same in both); Charges row 2+ = dropdown; Revenue row 2+ = disabled Select */}
        <div className='col-span-2'>
          <Label name={`${fieldPath}.${index}.label`} required={false}>
            Description
          </Label>
          {renderDescriptionField('text-sm')}
        </div>

        {/* UoM and Units in same row */}
        <div className='col-span-1'>
          <RHFTextInput
            name={`${fieldPath}.${index}.unitBasis`}
            label='UoM'
            required={false}
            inputClassName='text-sm'
            placeholder='Flat'
            readOnly={true}
          />
        </div>
        <div className='col-span-1'>
          <RHFTextInput
            name={`${fieldPath}.${index}.quantity`}
            label='Units'
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
            inputClassName='text-sm'
            placeholder='1'
            readOnly={true}
          />
        </div>

        {/* Rate and Total in same row */}
        <div className='col-span-1'>
          <Label name={`${fieldPath}.${index}.ratePerUnitUSD`} required={false}>
            Rate
          </Label>
          <RHFTextInput
            name={`${fieldPath}.${index}.ratePerUnitUSD`}
            label=''
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
            inputClassName='text-sm'
            placeholder='0.00'
            readOnly={true}
          />
        </div>
        <div className='col-span-1'>
          <RHFTextInput
            name={`${fieldPath}.${index}.totalChargeUSD`}
            label={isCarrierCostField ? 'Cost' : 'Revenue'}
            inputType='number'
            options={
              isCarrierCostField ? carrierFieldOptions : revenueFieldOptions
            }
            required={!isCarrierCostField}
            inputClassName='text-sm'
            placeholder='0.00'
          />
        </div>
      </>
    );
  }

  // Original horizontal layout (not used in sidebar, but kept for reference)
  return (
    <>
      {/* Description: Row 1 = same disabled Base Rate in both; row 2+ = dropdown (Charges) or disabled Select (Revenue) */}
      <div className='col-span-1 border-r border-neutral-200 p-2'>
        {renderDescriptionField('h-8 text-sm')}
      </div>

      {/* UoM */}
      <div className='col-span-1 border-r border-neutral-200 p-2'>
        <RHFTextInput
          name={`${fieldPath}.${index}.unitBasis`}
          label=''
          required={false}
          inputClassName='h-8 text-sm'
          placeholder='Flat'
          readOnly={true}
        />
      </div>

      {/* Units */}
      <div className='col-span-1 border-r border-neutral-200 p-2'>
        <RHFTextInput
          name={`${fieldPath}.${index}.quantity`}
          label=''
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
          inputClassName='h-8 text-sm'
          placeholder='1'
        />
      </div>

      {/* Rate */}
      <div className='col-span-1 border-r border-neutral-200 p-2'>
        <RHFTextInput
          name={`${fieldPath}.${index}.ratePerUnitUSD`}
          label=''
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
          inputClassName='h-8 text-sm'
          placeholder='0.00'
        />
      </div>

      {/* Cost/Revenue (editable total) */}
      <div className='col-span-1 border-r border-neutral-200 p-2'>
        <RHFTextInput
          name={`${fieldPath}.${index}.totalChargeUSD`}
          label=''
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
          inputClassName='h-8 text-sm'
          placeholder='0.00'
        />
      </div>
    </>
  );
}
